import React, { useEffect, useState } from 'react';

import RightColumn from '@editor/components/AuthComponent/RightColumn/RightColumn';
import OtpInput from '@editor/components/OTPInput/OTPInput';
import { FlexRow } from '@editor/components/utilities/layouts';
import { useProofOfWork } from '@editor/hooks/useProofOfWork';
import useWindowSize from '@editor/hooks/useWindowSize';
import { userStoreSelector, useUserStore } from '@editor/stores/userStore';
import {
  subscriptionStoreSelector,
  useSubscriptionStore,
} from '@repo/subscription/client';
import {
  Button,
  primarySmallTheme,
  primaryTheme,
} from '@repo/ui/components/Buttons';
import { TextInput, validators } from '@repo/ui/components/Inputs';
import { Spacer } from '@repo/ui/components/Spacer';
import { fireToast } from '@repo/ui/components/Toast';
import { H3, P2 } from '@repo/ui/components/Typography';
import { Logo } from '@repo/ui/deprecated/Logo';

import Api from '../../../api/user';

import { verifyEmailConfigs } from './config';
import { LeftColumn, CompactHeader, LogoWrapper } from './styles';

const VerifyEmailOtp: React.FC = () => {
  const user = useUserStore(userStoreSelector.user);
  const updateUserInfo = useUserStore(userStoreSelector.updateUserInfo);
  const { isMobile } = useWindowSize();
  const isInitialized = useSubscriptionStore(
    subscriptionStoreSelector.isInitialized
  );

  const { challenge, fetchChallenge, isChallengeReady } = useProofOfWork();
  const [otp, setOtp] = useState('');
  const [resendTimerStatus, setResendTimerStatus] = useState({
    canResendAt: new Date(),
  });

  const [otpVerifyRequestStatus, setOtpVerifyRequestStatus] = useState({
    isLoading: false,
    isError: false,
    errorMessage: '',
  });

  useEffect(() => {
    if (isInitialized) {
      fetchChallenge();
    }
  }, [fetchChallenge, isInitialized]);

  const resendEmailOtp = async (): Promise<void> => {
    if (challenge && isChallengeReady) {
      const result = await Api.requestEmailOtp({
        powChallenge: challenge,
      });
      fetchChallenge();
      fireToast({ label: 'We sent a new confirmation email!' });

      await updateUserInfo();

      if (result.success) {
        setResendTimerStatus({
          canResendAt: new Date(result.canResendAt),
        });
      }
    }
  };

  const verifyEmailOtp = async (): Promise<void> => {
    if (otpVerifyRequestStatus.isLoading) {
      return;
    }

    if (otp.length < verifyEmailConfigs.OTP_LENGTH) {
      setOtpVerifyRequestStatus({
        isError: true,
        errorMessage: `Enter the code that was sent to ${user?.email || 'your email'}`,
        isLoading: false,
      });
      return;
    }

    if (challenge && isChallengeReady) {
      setOtpVerifyRequestStatus({
        isLoading: true,
        isError: false,
        errorMessage: '',
      });

      const result = await Api.verifyEmailOtp({
        powChallenge: challenge,
        otp,
      });
      fetchChallenge();
      await updateUserInfo();

      setOtp('');
      if (result.success) {
        setOtpVerifyRequestStatus({
          isLoading: false,
          isError: false,
          errorMessage: '',
        });
      } else {
        setOtpVerifyRequestStatus({
          isLoading: false,
          isError: true,
          errorMessage: `Wrong Code. Enter the code that was sent to ${user?.email || 'your email'}`,
        });
      }
    }
  };

  useEffect(() => {
    const onFocus = (): void => {
      updateUserInfo();
    };

    window.addEventListener('focus', onFocus);

    return (): void => {
      window.removeEventListener('focus', onFocus);
    };
  }, [updateUserInfo]);

  const [userEmail, setUserEmail] = useState(user?.email || '');
  const [error, setError] = useState('');

  const handleValidation = (): void => {
    setError(validators.email(userEmail) ?? '');
  };

  const handleSubmit = async (): Promise<void> => {
    handleValidation();
    if (!error && user?.email !== userEmail) {
      const response = await Api.updateUserEmail({
        email: userEmail,
      } as never);
      if ('error' in response) {
        if (response.error === 'EMAIL_ALREADY_USED') {
          setError('This email is already in use.');
        }
        return;
      }
      if (response) {
        await updateUserInfo();
        fireToast({ label: 'We sent a new confirmation email!' });
      }
    }
  };

  const getResendWaitTimeInSeconds = (): number => {
    return Math.round(
      Math.max(
        0,
        resendTimerStatus.canResendAt.getTime() - new Date().getTime()
      ) / 1000
    );
  };

  return (
    <FlexRow height="100%" width="100%">
      <LeftColumn>
        <div>
          <LogoWrapper>
            {isMobile ? (
              <CompactHeader>
                <Logo style={{}} width={80} />
              </CompactHeader>
            ) : (
              <Logo />
            )}
          </LogoWrapper>
          <Spacer height="40px" />
          <H3>You&apos;re almost signed up</H3>
          <Spacer height="16px" />
          <TextInput
            autoComplete="username"
            errorMessage={error}
            name="email"
            placeholder="E-Mail"
            rightButton={{
              disabled: user?.email === userEmail,
              label: 'Change',
              onClick: handleSubmit,
            }}
            value={userEmail}
            onBlur={handleValidation}
            onChanging={setUserEmail}
            onEnter={handleSubmit}
          />
          <Spacer height="16px" />
          <P2>Enter the code sent to your email to finish signing up.</P2>
          <Spacer height="20px" />
          <OtpInput
            error={otpVerifyRequestStatus.errorMessage}
            length={verifyEmailConfigs.OTP_LENGTH}
            timeToResend={getResendWaitTimeInSeconds()}
            title="Code"
            value={otp}
            onChange={setOtp}
            onComplete={verifyEmailOtp}
            onResend={resendEmailOtp}
          />
          <Spacer height="8px" />
        </div>
        <div>
          <Spacer height="8px" />
          <Button
            disabled={
              otp.length < 6 ||
              !isChallengeReady ||
              otpVerifyRequestStatus.isLoading
            }
            label="Continue"
            loading={otpVerifyRequestStatus.isLoading}
            name="continueVerifyEmailOtpButton"
            style={{ width: isMobile ? '100%' : 'initial' }}
            theme={isMobile ? primarySmallTheme : primaryTheme}
            onClick={verifyEmailOtp}
          />
        </div>
      </LeftColumn>

      <RightColumn />
    </FlexRow>
  );
};

export default VerifyEmailOtp;
